import React from 'react';
import { Activity, AlertTriangle, Award, BarChart3, Brain, Clock, Heart, Loader2, TrendingUp, Users, Zap } from 'lucide-react';

const BenchmarkTab = ({ ctx }) => {
  const { benchmarkData } = ctx;

  return (
<div data-testid="benchmark-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Competitor Benchmarking</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Anonymous industry-wide comparison — see how your {benchmarkData?.industry || ''} team stacks up against {benchmarkData?.total_companies_benchmarked || 0}+ companies on the HiBFF platform.
  </p>

  {benchmarkData?.demo_data && (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
      <BarChart3 size={14} />
      Sample benchmark data — will use real cross-platform aggregates once more companies are active
    </div>
  )}

  {benchmarkData && (
    <>
      {/* Metric comparison cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { key: 'engagement_rate', label: 'Engagement', unit: '%', icon: Zap, color: '#F5831F' },
          { key: 'ocean_completion', label: 'OCEAN Done', unit: '%', icon: Brain, color: '#8B5CF6' },
          { key: 'pulse_score', label: 'Pulse Score', unit: '/5', icon: Heart, color: '#EC4899' },
          { key: 'retention_rate', label: 'Retention', unit: '%', icon: Users, color: '#059669' },
          { key: 'sessions_per_member_week', label: 'Sessions/Wk', unit: '', icon: Activity, color: '#3B82F6' },
          { key: 'confidence_lift', label: 'Confidence', unit: '%', icon: TrendingUp, color: '#10B981' },
          { key: 'badge_earn_rate', label: 'Badge Rate', unit: '%', icon: Award, color: '#F59E0B' },
          { key: 'avg_session_duration', label: 'Avg Duration', unit: 'min', icon: Clock, color: '#6366F1' },
        ].map(m => {
          const yours = benchmarkData.your_metrics[m.key];
          const avg = benchmarkData.industry_avg[m.key];
          const top = benchmarkData.top_quartile[m.key];
          const pct = benchmarkData.percentiles[m.key];
          const ahead = yours >= avg;
          return (
            <div key={m.key} className="bg-white rounded-xl p-4" style={{ border: `1px solid ${ahead ? '#D1FAE5' : '#FECACA'}` }} data-testid={`benchmark-${m.key}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <m.icon size={12} style={{ color: m.color }} />
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#999' }}>{m.label}</span>
              </div>
              <p className="text-2xl font-black" style={{ color: m.color }}>{yours}{m.unit}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[9px]" style={{ color: '#BBB' }}>Avg: {avg}{m.unit}</span>
                <span className="text-[9px]" style={{ color: '#BBB' }}>Top: {top}{m.unit}</span>
              </div>
              <div className="w-full h-1.5 rounded-full mt-2 relative" style={{ backgroundColor: '#F0F0F0' }}>
                <div className="absolute h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 75 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444' }} />
              </div>
              <p className="text-[9px] font-semibold text-right mt-1" style={{ color: pct >= 75 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444' }}>
                {pct}th percentile
              </p>
            </div>
          );
        })}
      </div>

      {/* Advantages vs Gaps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #D1FAE5' }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#065F46' }}>
            <TrendingUp size={14} style={{ color: '#10B981' }} /> Your Competitive Advantages
          </h3>
          <div className="space-y-2">
            {benchmarkData.advantages.map(a => (
              <div key={a.metric} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                <span className="text-xs font-medium" style={{ color: '#166534' }}>{a.metric}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold" style={{ color: '#059669' }}>{a.diff}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>P{a.percentile}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #FECACA' }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#991B1B' }}>
            <AlertTriangle size={14} style={{ color: '#EF4444' }} /> Areas for Improvement
          </h3>
          <div className="space-y-2">
            {benchmarkData.gaps.map(g => (
              <div key={g.metric} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEF2F2' }}>
                <span className="text-xs font-medium" style={{ color: '#991B1B' }}>{g.metric}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold" style={{ color: '#DC2626' }}>{g.diff}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}>P{g.percentile}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Peer Comparison Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }} data-testid="peer-table">
        <div className="px-5 py-3" style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Anonymous Peer Comparison ({benchmarkData.industry})</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
              {['Rank', 'Organisation', 'Engagement', 'OCEAN Done', 'Pulse', 'Sessions/Wk'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#999' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {benchmarkData.peer_comparison.map(p => (
              <tr key={p.name} style={{ borderBottom: '1px solid #F0F0F0', backgroundColor: p.is_you ? '#F5831F08' : undefined, borderLeft: p.is_you ? '3px solid #F5831F' : undefined }} data-testid={`peer-${p.rank}`}>
                <td className="px-4 py-2.5">
                  {p.rank <= 3 ? (
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: ['#F59E0B', '#94A3B8', '#CD7F32'][p.rank - 1] }}>{p.rank}</span>
                  ) : (
                    <span className="text-xs font-semibold pl-1" style={{ color: '#999' }}>{p.rank}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-sm font-semibold" style={{ color: p.is_you ? '#F5831F' : '#1A1209' }}>
                  {p.name} {p.is_you && <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase text-white ml-1" style={{ backgroundColor: '#F5831F' }}>YOU</span>}
                </td>
                <td className="px-4 py-2.5 text-xs font-bold" style={{ color: p.engagement >= 70 ? '#059669' : '#F59E0B' }}>{p.engagement}%</td>
                <td className="px-4 py-2.5 text-xs" style={{ color: '#666' }}>{p.ocean_completion}%</td>
                <td className="px-4 py-2.5 text-xs" style={{ color: '#666' }}>{p.pulse}/5</td>
                <td className="px-4 py-2.5 text-xs" style={{ color: '#666' }}>{p.sessions_week}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )}

  {!benchmarkData && (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm" style={{ color: '#999' }}>Loading benchmark data...</p>
    </div>
  )}
</div>
  );
};

export default BenchmarkTab;
